import {
  Identifier,
  IdentifierOmittedType,
  IdentifierType,
} from '@hooks/useAlbumInfo/types';
import { TrackDetails } from '@hooks/useTrackInfo/types';
import {
  formatIdentifierType,
  isIdentifierOmitted,
} from '@components/Release/helpers';
import { EntityLink } from '@components/shared/EntityLinksModal/types';
import { getUriId } from '@helpers/utils';

export const buildReleaseLinksFromTrackData = (trackData: TrackDetails) => {
  const artistReleaseLinks: EntityLink[] =
    trackData.artists?.map(artist => ({
      entity: artist.name,
      type: artist.role,
      uri: artist.uri,
      url: `https://open.spotify.com/artist/${getUriId(artist.uri)}`,
    })) || [];

  return [
    ...artistReleaseLinks,
    {
      entity: trackData.name,
      type: 'Track',
      uri: trackData.uri,
      url: trackData.url,
    },
  ];
};

// ISRC type renders first. ISWC type renders second if present.
// Other trackIdentifiers alphabetically sorted by type
export const sortAndOmitTrackIdentifiers = (
  identifierList: Identifier[],
): Identifier[] => {
  const sortedTrackIdentifiers = identifierList
    ? identifierList
        .filter(
          identifier =>
            !isIdentifierOmitted(identifier.type as IdentifierOmittedType),
        )
        .sort((a, b) => a.type.localeCompare(b.type))
        .map(identifier => ({
          type: formatIdentifierType(identifier.type),
          value: identifier.value,
        }))
    : [];

  const isrcIndex = sortedTrackIdentifiers.findIndex(
    ({ type }) => type === IdentifierType.ISRC,
  );

  const iswcIndex = sortedTrackIdentifiers.findIndex(
    ({ type }) => type === IdentifierType.ISWC,
  );

  if (isrcIndex !== -1) {
    const isrcItem = sortedTrackIdentifiers.splice(isrcIndex, 1)[0];
    sortedTrackIdentifiers.unshift(isrcItem);
  }

  if (iswcIndex !== -1) {
    const iswcItem = sortedTrackIdentifiers.splice(iswcIndex, 1)[0];
    sortedTrackIdentifiers.splice(1, 0, iswcItem);
  }

  return sortedTrackIdentifiers;
};

export const hasRelatedReleaseIsrc = (
  identifierList: Identifier[],
): Boolean => {
  return !!identifierList.find(
    ({ type }) => type.toLocaleUpperCase() === IdentifierType.ISRC,
  )?.value;
};
